import * as THREE from 'three'
import {
  BVHHelper,
  type ExtendedTriangle,
  INTERSECTED,
  MeshBVH,
  NOT_INTERSECTED,
} from 'three-mesh-bvh'
import { ParallelMeshBVHWorker } from 'three-mesh-bvh/worker'
import type {
  SpatialCapsule,
  SpatialHitMetadata,
  SpatialRepresentationDebugSnapshot,
} from '@/shared/types/spatial'
import type { NormalizedSpatialArtifact } from './normalizedSpatialArtifact'
import type {
  SpatialCollisionBackend,
  SpatialLocalCollisionGroundHit,
  SpatialLocalCollisionHit,
  SpatialLocalNavigationHit,
  SpatialLocalNavigationMotionResult,
  SpatialLocalSurfaceHit,
  SpatialNavigationBackend,
  SpatialSurfaceBackend,
} from './spatialBackend'

type DisposableBVHHelper = BVHHelper & {
  dispose(): void
}

interface SharedMeshBVHCore {
  key: string
  geometry: THREE.BufferGeometry
  bvh: MeshBVH
  triangleMetadata: ReadonlyArray<SpatialHitMetadata>
}

interface SharedMeshBVHCoreEntry {
  core: SharedMeshBVHCore | null
  building: Promise<SharedMeshBVHCore> | null
  refCount: number
}

const sharedMeshBVHCoreEntries = new Map<string, SharedMeshBVHCoreEntry>()
const debugWireframeGeometryCache = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>()
const debugBoundsGeometryCache = new WeakMap<
  MeshBVH,
  Map<number, ReadonlyArray<THREE.BufferGeometry>>
>()

type MeshBVHWorkerHandle = {
  dispose(): void
  generate(
    geometry: THREE.BufferGeometry,
    options: {
      maxLeafSize: number
      setBoundingBox: boolean
      indirect: boolean
    }
  ): Promise<MeshBVH>
}

/**
 * 基于 three-mesh-bvh 的空间查询后端，实现表面/碰撞/导航三类接口。
 */
export class MeshBVHBackend
  implements SpatialSurfaceBackend, SpatialCollisionBackend, SpatialNavigationBackend
{
  /** 后端类型标识 */
  readonly kind = 'mesh-bvh'
  /** 共享几何体，包含本地坐标顶点 */
  readonly geometry: THREE.BufferGeometry
  /** 已构建的 MeshBVH 实例 */
  readonly bvh: MeshBVH

  private readonly sharedCore: SharedMeshBVHCore
  private readonly triangleMetadata: ReadonlyArray<SpatialHitMetadata>
  private disposed = false

  /**
   * 异步构建 MeshBVH 后端实例，复用同源 artifact 的共享核心数据。
   *
   * @param artifact - 归一化空间几何制品，提供共享键与三角形元数据
   * @returns 构建完成的后端实例
   */
  static async create(artifact: NormalizedSpatialArtifact): Promise<MeshBVHBackend> {
    const sharedCore = await acquireSharedMeshBVHCore(artifact)
    return new MeshBVHBackend(sharedCore)
  }

  private constructor(sharedCore: SharedMeshBVHCore) {
    this.sharedCore = sharedCore
    this.geometry = this.sharedCore.geometry
    this.bvh = this.sharedCore.bvh
    this.triangleMetadata = this.sharedCore.triangleMetadata
  }

  /** 获取 canonical local geometry，用于共享遮挡深度写入。 */
  /**
   * Gets canonical local geometry for shared occlusion depth.
   *
   * @returns Shared local geometry, or null when unavailable.
   */
  getDepthGeometry(): THREE.BufferGeometry | null {
    return this.geometry
  }

  /**
   * 以射线求最近命中面片。
   *
   * @param ray - 本地坐标系射线
   * @returns 最近命中信息；未命中返回 null
   */
  raycastFirst(ray: THREE.Ray): SpatialLocalSurfaceHit | null {
    const intersection = this.bvh.raycastFirst(ray, THREE.DoubleSide, 0, Number.POSITIVE_INFINITY)
    if (!intersection) {
      return null
    }

    return this.createSurfaceHit(
      intersection.point.clone(),
      intersection.distance,
      intersection.faceIndex ?? undefined,
      intersection.face?.normal?.clone(),
      intersection
    )
  }

  /**
   * 查询距离给定点最近的表面点。
   *
   * @param point - 查询点，本地坐标
   * @param maxDistance - 搜索半径上限，超出返回 null；默认无穷
   * @returns 命中信息；超出距离或无结果返回 null
   */
  closestPoint(
    point: THREE.Vector3,
    maxDistance = Number.POSITIVE_INFINITY
  ): SpatialLocalSurfaceHit | SpatialLocalCollisionHit | null {
    const target = {
      point: new THREE.Vector3(),
      distance: Number.POSITIVE_INFINITY,
      faceIndex: -1,
    }
    const result = this.bvh.closestPointToPoint(point, target, 0, maxDistance) as {
      point?: THREE.Vector3
      distance?: number
      faceIndex?: number
    } | null

    if (!result?.point || result.distance === undefined) {
      return null
    }

    return this.createSurfaceHit(
      result.point.clone(),
      result.distance,
      result.faceIndex,
      result.faceIndex !== undefined ? this.getFaceNormal(result.faceIndex) : undefined,
      result
    )
  }

  /**
   * 球体与表面重叠测试。
   *
   * @param sphere - 查询球体，本地坐标
   * @returns 碰撞信息含穿透深度与分离法线；未穿透返回 null
   */
  overlapSphere(sphere: THREE.Sphere): SpatialLocalCollisionHit | null {
    const workingSphere = sphere.clone()
    const originalCenter = sphere.center.clone()
    const collector: { hit: TriangleCollisionHit | null; count: number } = {
      hit: null,
      count: 0,
    }

    this.forEachSphereCandidateTriangle(sphere, (triangle, triangleIndex) => {
      const hit = triangleSphereIntersect(workingSphere, triangle)
      if (!hit || hit.depth <= CONTACT_EPSILON) {
        return
      }

      collector.count += 1
      workingSphere.center.addScaledVector(hit.normal, hit.depth)
      if (!collector.hit || hit.depth > collector.hit.depth) {
        collector.hit = {
          ...hit,
          faceIndex: triangleIndex,
          queryDistance: Math.max(0, sphere.radius - hit.depth),
        }
      }
    })

    const resolvedHit = collector.hit
    if (!resolvedHit) {
      return null
    }

    const collisionVector = workingSphere.center.sub(originalCenter)
    const penetrationDepth = collisionVector.length()
    if (penetrationDepth <= CONTACT_EPSILON) {
      return null
    }

    const separationNormal = collisionVector.normalize()

    return {
      ...this.createCollisionHit(
        resolvedHit.point.clone(),
        resolvedHit.queryDistance,
        resolvedHit.faceIndex,
        resolvedHit.normal,
        {
          sphere,
          hitCount: collector.count,
        }
      ),
      penetrationDepth,
      separationNormal,
    }
  }

  /**
   * 胶囊体与表面重叠测试。
   *
   * @param capsule - 查询胶囊体，本地坐标
   * @returns 碰撞信息含穿透深度与分离法线；未穿透返回 null
   */
  overlapCapsule(capsule: SpatialCapsule): SpatialLocalCollisionHit | null {
    const workingCapsule = cloneCapsule(capsule)
    const originalCenter = getCapsuleCenter(capsule, new THREE.Vector3())
    const collector: { hit: TriangleCollisionHit | null; count: number } = {
      hit: null,
      count: 0,
    }

    this.forEachCapsuleCandidateTriangle(capsule, (triangle, triangleIndex) => {
      const hit = triangleCapsuleIntersect(workingCapsule, triangle)
      if (!hit || hit.depth <= CONTACT_EPSILON) {
        return
      }

      collector.count += 1
      translateCapsuleInPlace(workingCapsule, hit.normal.clone().multiplyScalar(hit.depth))
      if (!collector.hit || hit.depth > collector.hit.depth) {
        collector.hit = {
          ...hit,
          faceIndex: triangleIndex,
          queryDistance: Math.max(0, capsule.radius - hit.depth),
        }
      }
    })

    const resolvedHit = collector.hit
    if (!resolvedHit) {
      return null
    }

    const collisionVector = getCapsuleCenter(workingCapsule, new THREE.Vector3()).sub(
      originalCenter
    )
    const penetrationDepth = collisionVector.length()
    if (penetrationDepth <= CONTACT_EPSILON) {
      return null
    }

    return {
      ...this.createCollisionHit(
        resolvedHit.point.clone(),
        resolvedHit.queryDistance,
        resolvedHit.faceIndex,
        resolvedHit.normal,
        {
          capsule,
          hitCount: collector.count,
        }
      ),
      penetrationDepth,
      separationNormal: collisionVector.normalize(),
    }
  }

  /**
   * 沿位移矢量扫掠胶囊体，查找最先发生碰撞的位置。
   *
   * @param capsule - 初始胶囊体
   * @param motion - 位移矢量，本地坐标
   * @param maxSteps - 线性扫掠采样步数，默认 24；二分再细化
   * @returns 碰撞信息含行进距离；未碰撞返回 null
   */
  sweepCapsule(
    capsule: SpatialCapsule,
    motion: THREE.Vector3,
    maxSteps = 24
  ): SpatialLocalCollisionHit | null {
    const overlap = this.overlapCapsule(capsule)
    if (overlap) {
      return {
        ...overlap,
        travelDistance: 0,
      }
    }

    const motionLength = motion.length()
    if (motionLength <= 1e-8) {
      return null
    }

    const steps = Math.max(2, Math.round(maxSteps))
    let low = 0
    let high = 1
    let hit: SpatialLocalCollisionHit | null = null

    for (let step = 1; step <= steps; step += 1) {
      const t = step / steps
      const moved = translateCapsule(capsule, motion, t)
      const candidate = this.overlapCapsule(moved)
      if (candidate) {
        low = (step - 1) / steps
        high = t
        hit = candidate
        break
      }
    }

    if (!hit) {
      return null
    }

    for (let iteration = 0; iteration < 5; iteration += 1) {
      const mid = (low + high) * 0.5
      const moved = translateCapsule(capsule, motion, mid)
      const candidate = this.overlapCapsule(moved)
      if (candidate) {
        high = mid
        hit = candidate
      } else {
        low = mid
      }
    }

    return {
      ...hit,
      travelDistance: motionLength * high,
    }
  }

  /**
   * 沿向下方向查找最近可站立的地面点。
   *
   * @param origin - 检测起点
   * @param down - 向下方向，默认 (0, -1, 0)
   * @param maxDistance - 最大距离，默认无穷
   * @param maxSlopeAngle - 最大允许坡度，单位度；超出返回 null
   * @returns 地面命中信息含坡度；未命中或超坡度返回 null
   */
  resolveGround(
    origin: THREE.Vector3,
    down = DEFAULT_DOWN,
    maxDistance = Number.POSITIVE_INFINITY,
    maxSlopeAngle?: number
  ): SpatialLocalCollisionGroundHit | null {
    const direction = down.clone()
    if (direction.lengthSq() <= 1e-8) {
      return null
    }

    direction.normalize()
    const hit = this.raycastFirst(new THREE.Ray(origin.clone(), direction))
    if (!hit || hit.distance > maxDistance) {
      return null
    }

    const slopeAngle = hit.normal
      ? THREE.MathUtils.radToDeg(hit.normal.angleTo(direction.clone().negate()))
      : undefined
    if (slopeAngle !== undefined && maxSlopeAngle !== undefined && slopeAngle > maxSlopeAngle) {
      return null
    }

    return {
      ...this.createCollisionHit(hit.point, hit.distance, hit.faceIndex, hit.normal, hit.raw),
      slopeAngle,
    }
  }

  /**
   * 在导航面上投影到最近点。
   *
   * @param point - 查询点
   * @param maxDistance - 最大距离上限，默认无穷
   * @param requiredTags - 必需语义标签，命中三角形需全部具备
   * @param blockedTags - 禁止语义标签，命中三角形不得包含
   * @returns 导航命中含语义与 walkable 标记；未命中返回 null
   */
  projectPoint(
    point: THREE.Vector3,
    maxDistance = Number.POSITIVE_INFINITY,
    requiredTags?: ReadonlyArray<string>,
    blockedTags?: ReadonlyArray<string>
  ): SpatialLocalNavigationHit | null {
    const closest = this.findClosestFilteredPoint(point, maxDistance, (metadata) =>
      isNavigationMetadataAllowed(metadata, requiredTags, blockedTags)
    )
    if (!closest) {
      return null
    }

    return {
      ...closest,
      semanticTags: closest.metadata?.semantic.tags ?? [],
      walkable: isMetadataWalkable(closest.metadata),
    }
  }

  /**
   * 沿位移解算可通过的终点位置。
   *
   * @param start - 起点
   * @param motion - 期望位移矢量
   * @param maxDistance - 每步导航最大距离，默认无穷
   * @param requiredTags - 必需语义标签
   * @param blockedTags - 禁止语义标签
   * @param samples - 线性采样数，默认 12
   * @returns 导航运动结果，含最终位置与 blocked 标记；全程受阻返回 null
   */
  resolveMotion(
    start: THREE.Vector3,
    motion: THREE.Vector3,
    maxDistance = Number.POSITIVE_INFINITY,
    requiredTags?: ReadonlyArray<string>,
    blockedTags?: ReadonlyArray<string>,
    samples = 12
  ): SpatialLocalNavigationMotionResult | null {
    const totalSamples = Math.max(1, Math.round(samples))
    for (let step = totalSamples; step >= 0; step -= 1) {
      const t = step / totalSamples
      const candidatePoint = start.clone().addScaledVector(motion, t)
      const hit = this.projectPoint(candidatePoint, maxDistance, requiredTags, blockedTags)
      if (!hit) {
        continue
      }

      return {
        ...hit,
        resolvedPoint: hit.point.clone(),
        appliedMotion: hit.point.clone().sub(start),
        blocked: step !== totalSamples,
      }
    }

    return null
  }

  /**
   * 创建调试可视化原型节点。
   *
   * @param name - 节点名称前缀
   * @param debug - 调试快照，控制 mesh/bvh 可见性与层数
   * @returns 包含 mesh 与 BVH 辅助节点的 Group；无需显示返回仍为 Group
   */
  createDebugPrototype(
    name: string,
    debug: SpatialRepresentationDebugSnapshot
  ): THREE.Object3D | null {
    if (!debug.meshVisible && !debug.boundsVisible) {
      return null
    }

    const root = new THREE.Group()
    root.name = name

    if (debug.meshVisible) {
      root.add(createDebugMesh(this.geometry, `${name}-mesh`))
    }

    if (debug.boundsVisible) {
      root.add(createBoundsHelper(this.bvh, `${name}-bvh`, debug.boundsDepth))
    }

    return root
  }

  /** 释放后端，归还共享 BVH 引用并释放几何体 */
  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    releaseSharedMeshBVHCore(this.sharedCore)
  }

  private createSurfaceHit(
    point: THREE.Vector3,
    distance: number,
    faceIndex?: number,
    normal?: THREE.Vector3,
    raw?: unknown
  ): SpatialLocalSurfaceHit {
    return {
      point,
      distance,
      faceIndex,
      normal: normal?.clone(),
      metadata: faceIndex !== undefined ? this.triangleMetadata[faceIndex] : undefined,
      raw,
    }
  }

  private createCollisionHit(
    point: THREE.Vector3,
    distance: number,
    faceIndex?: number,
    normal?: THREE.Vector3,
    raw?: unknown
  ): SpatialLocalCollisionHit {
    return {
      point,
      distance,
      faceIndex,
      normal: normal?.clone(),
      metadata: faceIndex !== undefined ? this.triangleMetadata[faceIndex] : undefined,
      raw,
    }
  }

  private findClosestFilteredPoint(
    point: THREE.Vector3,
    maxDistance: number,
    predicate: (metadata: SpatialHitMetadata | undefined) => boolean
  ): SpatialLocalNavigationHit | null {
    const triangle = new THREE.Triangle()
    const closestPoint = new THREE.Vector3()
    let bestDistanceSq = Number.isFinite(maxDistance)
      ? maxDistance * maxDistance
      : Number.POSITIVE_INFINITY
    let bestHit: SpatialLocalNavigationHit | null = null

    for (let faceIndex = 0; faceIndex < this.triangleMetadata.length; faceIndex += 1) {
      const metadata = this.triangleMetadata[faceIndex]
      if (!predicate(metadata)) {
        continue
      }

      this.populateTriangle(faceIndex, triangle)
      triangle.closestPointToPoint(point, closestPoint)
      const distanceSq = closestPoint.distanceToSquared(point)
      if (distanceSq > bestDistanceSq) {
        continue
      }

      const normal = triangle.getNormal(new THREE.Vector3()).normalize()
      bestDistanceSq = distanceSq
      bestHit = {
        point: closestPoint.clone(),
        distance: Math.sqrt(distanceSq),
        faceIndex,
        normal,
        metadata,
        semanticTags: metadata.semantic.tags,
        walkable: isMetadataWalkable(metadata),
      }
    }

    return bestHit
  }

  private getFaceNormal(faceIndex: number): THREE.Vector3 {
    const triangle = new THREE.Triangle()
    this.populateTriangle(faceIndex, triangle)
    return triangle.getNormal(new THREE.Vector3()).normalize()
  }

  private forEachSphereCandidateTriangle(
    sphere: THREE.Sphere,
    callback: (triangle: ExtendedTriangle, triangleIndex: number) => void
  ): void {
    this.bvh.shapecast({
      intersectsBounds: (box) => (sphere.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED),
      intersectsTriangle: (triangle, triangleIndex) => {
        callback(triangle, triangleIndex)
        return false
      },
    })
  }

  private forEachCapsuleCandidateTriangle(
    capsule: SpatialCapsule,
    callback: (triangle: ExtendedTriangle, triangleIndex: number) => void
  ): void {
    const queryBounds = getCapsuleBounds(capsule, scratchCapsuleBounds)
    this.bvh.shapecast({
      intersectsBounds: (box) => (queryBounds.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED),
      intersectsTriangle: (triangle, triangleIndex) => {
        callback(triangle, triangleIndex)
        return false
      },
    })
  }

  private populateTriangle(faceIndex: number, target: THREE.Triangle): void {
    const position = this.geometry.getAttribute('position') as THREE.BufferAttribute
    const index = this.geometry.index
    const first = faceIndex * 3

    const aIndex = index ? index.getX(first) : first
    const bIndex = index ? index.getX(first + 1) : first + 1
    const cIndex = index ? index.getX(first + 2) : first + 2

    target.a.fromBufferAttribute(position, aIndex)
    target.b.fromBufferAttribute(position, bIndex)
    target.c.fromBufferAttribute(position, cIndex)
  }
}

function createDebugMesh(geometry: THREE.BufferGeometry, name: string): THREE.LineSegments {
  const wireframeGeometry = getCachedDebugWireframeGeometry(geometry)
  const material = new THREE.LineBasicMaterial({
    color: 0x17a34a,
    depthWrite: false,
    transparent: true,
    opacity: 0.2,
  })
  const lineSegments = new THREE.LineSegments(wireframeGeometry, material)
  lineSegments.name = name
  lineSegments.frustumCulled = false
  return lineSegments
}

function createBoundsHelper(bvh: MeshBVH, name: string, depth: number): DisposableBVHHelper {
  const normalizedDepth = normalizeBoundsDepth(depth)
  const geometries = getCachedDebugBoundsGeometries(bvh, normalizedDepth)

  const helper = new THREE.Group() as DisposableBVHHelper
  helper.name = name
  helper.depth = normalizedDepth

  for (const sourceGeometry of geometries) {
    const material = new THREE.LineBasicMaterial({
      color: 0xf97316,
      depthWrite: false,
      transparent: true,
      opacity: 0.5,
    })
    const lineSegments = new THREE.LineSegments(sourceGeometry, material)
    lineSegments.name = 'BVHRootHelper'
    lineSegments.frustumCulled = false
    helper.add(lineSegments)
  }

  // Geometry is cached on the shared BVH core and released with the core.
  helper.dispose = () => {}

  return helper
}

function getCachedDebugWireframeGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const cached = debugWireframeGeometryCache.get(geometry)
  if (cached) {
    return cached
  }

  const wireframeGeometry = new THREE.WireframeGeometry(geometry)
  debugWireframeGeometryCache.set(geometry, wireframeGeometry)
  return wireframeGeometry
}

function getCachedDebugBoundsGeometries(
  bvh: MeshBVH,
  depth: number
): ReadonlyArray<THREE.BufferGeometry> {
  let cache = debugBoundsGeometryCache.get(bvh)
  if (!cache) {
    cache = new Map()
    debugBoundsGeometryCache.set(bvh, cache)
  }

  const cached = cache.get(depth)
  if (cached) {
    return cached
  }

  const source = new BVHHelper(bvh, depth) as DisposableBVHHelper
  source.color.set(0xf97316)
  source.opacity = 0.5
  source.displayEdges = true
  source.displayParents = false
  source.update()

  const geometries: THREE.BufferGeometry[] = []
  for (const sourceRoot of source.children) {
    const sourceGeometry = (sourceRoot as THREE.Object3D & { geometry?: THREE.BufferGeometry })
      .geometry
    if (!sourceGeometry) {
      continue
    }

    geometries.push(sourceGeometry)
  }

  source.clear()
  source.dispose()
  cache.set(depth, geometries)
  return geometries
}

function translateCapsule(
  capsule: SpatialCapsule,
  motion: THREE.Vector3,
  t: number
): SpatialCapsule {
  const delta = motion.clone().multiplyScalar(t)
  return {
    start: capsule.start.clone().add(delta),
    end: capsule.end.clone().add(delta),
    radius: capsule.radius,
  }
}

interface TriangleCollisionHit {
  normal: THREE.Vector3
  point: THREE.Vector3
  depth: number
  faceIndex: number
  queryDistance: number
}

type TriangleIntersectionResult = {
  normal: THREE.Vector3
  point: THREE.Vector3
  depth: number
}

function triangleCapsuleIntersect(
  capsule: SpatialCapsule,
  triangle: ExtendedTriangle
): TriangleIntersectionResult | null {
  scratchLine1.set(capsule.start, capsule.end)
  const distance = triangle.closestPointToSegment(scratchLine1, scratchPoint1, scratchPoint2)
  if (distance >= capsule.radius - CONTACT_EPSILON) {
    return null
  }

  const normal = scratchPoint2.clone().sub(scratchPoint1)
  if (normal.lengthSq() <= LINE_EPSILON) {
    normal.copy(triangle.getNormal(new THREE.Vector3()))
  } else {
    normal.normalize()
  }

  return {
    normal,
    point: scratchPoint1.clone(),
    depth: capsule.radius - distance,
  }
}

function triangleSphereIntersect(
  sphere: THREE.Sphere,
  triangle: ExtendedTriangle
): TriangleIntersectionResult | null {
  triangle.closestPointToPoint(sphere.center, scratchPoint1)
  const distance = scratchPoint1.distanceTo(sphere.center)
  if (distance >= sphere.radius - CONTACT_EPSILON) {
    return null
  }

  const normal = sphere.center.clone().sub(scratchPoint1)
  if (normal.lengthSq() <= LINE_EPSILON) {
    normal.copy(triangle.getNormal(new THREE.Vector3()))
  } else {
    normal.normalize()
  }

  return {
    normal,
    point: scratchPoint1.clone(),
    depth: sphere.radius - distance,
  }
}

function getCapsuleBounds(capsule: SpatialCapsule, target: THREE.Box3): THREE.Box3 {
  target.makeEmpty()
  target.expandByPoint(capsule.start)
  target.expandByPoint(capsule.end)
  target.expandByScalar(capsule.radius)
  return target
}

function getCapsuleCenter(capsule: SpatialCapsule, target: THREE.Vector3): THREE.Vector3 {
  return target.copy(capsule.start).add(capsule.end).multiplyScalar(0.5)
}

function cloneCapsule(capsule: SpatialCapsule): SpatialCapsule {
  return {
    start: capsule.start.clone(),
    end: capsule.end.clone(),
    radius: capsule.radius,
  }
}

function translateCapsuleInPlace(capsule: SpatialCapsule, motion: THREE.Vector3): void {
  capsule.start.add(motion)
  capsule.end.add(motion)
}

function isMetadataWalkable(metadata: SpatialHitMetadata | undefined): boolean {
  if (!metadata) {
    return true
  }
  if (metadata.semantic.blocked) {
    return false
  }
  if (metadata.semantic.walkable === false) {
    return false
  }
  return true
}

function isNavigationMetadataAllowed(
  metadata: SpatialHitMetadata | undefined,
  requiredTags?: ReadonlyArray<string>,
  blockedTags?: ReadonlyArray<string>
): boolean {
  if (!isMetadataWalkable(metadata)) {
    return false
  }

  const tags = new Set(metadata?.semantic.tags ?? [])
  if (blockedTags?.some((tag) => tags.has(tag))) {
    return false
  }
  if (requiredTags?.length) {
    return requiredTags.every((tag) => tags.has(tag))
  }

  return true
}

function normalizeBoundsDepth(depth: number): number {
  if (!Number.isFinite(depth)) {
    return 10
  }

  return Math.max(1, Math.round(depth))
}

function acquireSharedMeshBVHCore(artifact: NormalizedSpatialArtifact): Promise<SharedMeshBVHCore> {
  const existing = sharedMeshBVHCoreEntries.get(artifact.source.key)
  if (existing?.core) {
    existing.refCount += 1
    return Promise.resolve(existing.core)
  }

  if (existing?.building) {
    return existing.building.then((core) => {
      existing.refCount += 1
      return core
    })
  }

  const entry: SharedMeshBVHCoreEntry = existing ?? {
    core: null,
    building: null,
    refCount: 0,
  }
  sharedMeshBVHCoreEntries.set(artifact.source.key, entry)
  entry.building = buildSharedMeshBVHCore(artifact)
    .then((core) => {
      entry.core = core
      entry.building = null
      return core
    })
    .catch((error) => {
      entry.building = null
      sharedMeshBVHCoreEntries.delete(artifact.source.key)
      throw error
    })

  return entry.building.then((core) => {
    entry.refCount += 1
    return core
  })
}

function releaseSharedMeshBVHCore(core: SharedMeshBVHCore): void {
  const entry = sharedMeshBVHCoreEntries.get(core.key)
  if (!entry || entry.core !== core) {
    return
  }

  entry.refCount = Math.max(0, entry.refCount - 1)
  if (entry.refCount > 0) {
    return
  }

  disposeDebugGeometryCache(entry.core.geometry, entry.core.bvh)
  entry.core.geometry.dispose()
  sharedMeshBVHCoreEntries.delete(core.key)
}

function disposeDebugGeometryCache(geometry: THREE.BufferGeometry, bvh: MeshBVH): void {
  debugWireframeGeometryCache.get(geometry)?.dispose()
  debugWireframeGeometryCache.delete(geometry)

  const boundsGeometries = debugBoundsGeometryCache.get(bvh)
  if (boundsGeometries) {
    for (const entries of boundsGeometries.values()) {
      for (const boundsGeometry of entries) {
        boundsGeometry.dispose()
      }
    }
    debugBoundsGeometryCache.delete(bvh)
  }
}

async function buildSharedMeshBVHCore(
  artifact: NormalizedSpatialArtifact
): Promise<SharedMeshBVHCore> {
  const geometry = artifact.geometry.clone()
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()

  const bvh = artifact.serializedBVH
    ? MeshBVH.deserialize(artifact.serializedBVH, geometry)
    : await createMeshBVH(geometry)
  artifact.exportArtifact?.(geometry, bvh)
  return {
    key: artifact.source.key,
    geometry,
    bvh,
    triangleMetadata: artifact.triangleMetadata,
  }
}

async function createMeshBVH(geometry: THREE.BufferGeometry): Promise<MeshBVH> {
  const worker = createMeshBVHWorker()
  if (!worker) {
    return createMeshBVHSync(geometry)
  }

  try {
    return await worker.generate(geometry, MESH_BVH_BUILD_OPTIONS)
  } finally {
    worker.dispose()
  }
}

function createMeshBVHSync(geometry: THREE.BufferGeometry): MeshBVH {
  return new MeshBVH(geometry, MESH_BVH_BUILD_OPTIONS)
}

function createMeshBVHWorker(): MeshBVHWorkerHandle | null {
  if (
    typeof Worker === 'undefined' ||
    typeof SharedArrayBuffer === 'undefined' ||
    globalThis.crossOriginIsolated !== true
  ) {
    return null
  }

  try {
    return new ParallelMeshBVHWorker() as unknown as MeshBVHWorkerHandle
  } catch {
    return null
  }
}

const MESH_BVH_BUILD_OPTIONS = {
  maxLeafSize: 10,
  setBoundingBox: true,
  indirect: true,
} as const

const LINE_EPSILON = 1e-10
const CONTACT_EPSILON = 1e-5
const _DEFAULT_UP = new THREE.Vector3(0, 1, 0)
const DEFAULT_DOWN = new THREE.Vector3(0, 0, -1)
const scratchCapsuleBounds = new THREE.Box3()
const scratchLine1 = new THREE.Line3()
const scratchPoint1 = new THREE.Vector3()
const scratchPoint2 = new THREE.Vector3()
